class CTOCartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CTOCartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEvents = this.addEvents.bind(this);
    this.$footer = this.querySelector('.js-cart-items-footer');
    this.scrollPosition = 0;
    // add mobile scroll event only if apple pay is supported
    if (window?.ApplePaySession && this.$footer.querySelector('.apple-button')) {
      this.$footer.classList.add('apple-pay-supported');
      if (this.$footer.querySelector('.checkout-button')) {
        this.$footer.classList.add('checkout-button-supported');
      }
      this.querySelector('cart-items').addEventListener('scroll', this.onScroll.bind(this));
    }

    this.ctoItemKey = null;
    this.currentItemsSkus = {};
    this.currentItemsAll = {};
    this.currentSkuKeyPair = {};
  }

  onScroll(event) {
    if (window.innerWidth < 768) {
      if (event.target.scrollTop >= this.scrollPosition) {
        this.$footer.classList.add('scrolled');
      } else {
        this.$footer.classList.remove('scrolled');
      }
      this.scrollPosition = event.target.scrollTop;
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      // scroll to top of the cart items
      this.querySelector('cart-items').scrollTop = 0;
      this.querySelector('.drawer__inner').scrollTop = 0;
      this.scrollPosition = 0;
      this.$footer.classList.remove('scrolled');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CTOCartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    this.currentSkuKeyPair = {};
    this.currentItemsAll = {};
    document.getElementById('cart-icon-bubble')?.focus();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  getInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  groupValidItemsBySku = (cartDrawerElement) => {
    const currentSkuMap = {};

    Object.keys(this.currentSkuKeyPair).forEach((key) => {
      const sku = this.currentSkuKeyPair[key];
      const $item = cartDrawerElement.querySelector(`.cto-cart-item[data-sku="${sku}"][data-cto-cart-item-key="${key}"]`);

      if ($item) {
        let itemQty = parseInt($item.querySelector('.quantity__input')?.value, 10) || 1;
        // initialize this data object if new SKU found
        if (!currentSkuMap[sku]) {
          currentSkuMap[sku] = {
            keys: [],
            quantity: 0
          };
        }

        // Add the key to the list and increment the pre-calculated quantity.
        currentSkuMap[sku].keys.push(key);
        currentSkuMap[sku].quantity += itemQty;
      }
    });

    return currentSkuMap;
  };

  updateLatestCartItems() {
    const $items = this.querySelectorAll(`.cto-cart-item[data-cto-cart-item-key="${this.ctoItemKey}"]`);

    if ($items?.length) {
      $items.forEach(($item) => {
        $item.classList.add('active');
      });
    }

    // Show insurance section if there are active insurance items
    const $insuranceSection = this.querySelector('.js-cto-insurance')
    const $insuranceItems = $insuranceSection.querySelectorAll(`.cto-cart-item.active`);
    if ($insuranceItems?.length) {
      $insuranceSection.classList.remove('hidden');
    }

    // Show GWP section if there are active items
    const $gwpSection = this.querySelector('.js-cto-gwp')
    const $gwpItems = $gwpSection.querySelectorAll(`.cto-cart-item.active`);
    if ($gwpItems?.length) {
      $gwpSection.classList.remove('hidden');
    }

    const $offerSection = this.querySelector('.js-cto-offers');
    const $offerItems = $offerSection?.querySelector(`.cto-cart-item.active`)|| null;
      if ($offerItems) {
        this.refreshCarouselEvents();
      }

    const $mainCartItems = this.querySelectorAll('.js-cart-items-body > .cart-items .cto-cart-item');
    const $mainCartItemsWrapper = this.querySelector('.js-cart-items-body > .cart-items');
    $mainCartItems.forEach(($item) => {
      $item.classList.remove('active');
    });
    const ctoGwpItems = $gwpSection.querySelectorAll('.cto-gwp-item');
    const currentSkuMap = this.groupValidItemsBySku($mainCartItemsWrapper);

    Object.keys(currentSkuMap).forEach((sku) => {
      const itemSkuData = currentSkuMap[sku];
      const itemQty = itemSkuData.quantity;
      let itemKey = itemSkuData.keys[0]; // get first available key to show in the Drawer
      
      if (itemQty > 1) {
        let longestGwpSkuLength = -1;
        // Update the item-qty-grouped data attr for grouped items with the same SKU (itemSkuData.keys).
        itemSkuData.keys.forEach(groupedKey => {
          const elem = $mainCartItemsWrapper.querySelector(`.cto-cart-item[data-sku="${sku}"][data-cto-cart-item-key="${groupedKey}"]`);
          const groupedItemQtyElem = elem.querySelector('.js-cart-item-qty');
          const itemAplcr = elem.querySelector('.cart-item__row--care');
          const itemAplcrQtyElem = itemAplcr?.querySelector('.js-cart-item-qty');
          const currentGwpKey = elem?.dataset.gwp_key;
          const currentGwpSkuDataset = elem?.dataset.gwp_sku;
          const currentGwpSkuLength = currentGwpSkuDataset?.length ?? -1;

          groupedItemQtyElem?.setAttribute('data-item-qty-grouped', itemQty);

          if (currentGwpSkuLength && currentGwpSkuLength > longestGwpSkuLength) {
            const currentGwpSku = currentGwpSkuDataset?.split(',')[0];  // get first available SKU if multiple SKUs
            const isGwpItemValid = $mainCartItemsWrapper.querySelector(`.cto-cart-item[data-sku="${currentGwpSku}"][data-gwp_key="${currentGwpKey}"]`);

            if (isGwpItemValid) {
              longestGwpSkuLength = currentGwpSkuLength;
              itemKey = groupedKey;
            }
          }
           // update AppleCare grouped qty
          if (itemAplcr && itemAplcrQtyElem) {
            itemAplcrQtyElem.textContent = `(${itemQty})`;
            itemAplcrQtyElem.setAttribute('data-item-qty-grouped', itemQty);
          }
        });
        
        // update gwpOffers with relavant CTO grouped active item
        const isGwpItemValidKey = Array.from(ctoGwpItems)?.some(gwp_item => gwp_item.dataset.ctoCartItemKey === itemKey);
        if (isGwpItemValidKey) {
          ctoGwpItems.forEach(gwp_item => {
            gwp_item.classList.toggle('active', gwp_item.dataset.ctoCartItemKey === itemKey);
          });
        }

        if ($gwpItems?.length) {
          $gwpSection.classList.remove('hidden');
        } else {
          $gwpSection.classList.add('hidden');
        }
      }

      const item = $mainCartItemsWrapper.querySelector(`.cto-cart-item[data-sku="${sku}"][data-cto-cart-item-key="${itemKey}"]`);

      if (item) {
        item.classList.add('active');
        item.querySelector('.cart-item__name').focus();

        const itemQtyElem = item.querySelector('.js-cart-item-qty');
        if (itemQtyElem) {
          itemQtyElem.textContent = `(${itemQty})`;
          itemQtyElem.setAttribute('data-item-qty-grouped', itemQty);
        }

        const $bundleItems = $mainCartItemsWrapper.querySelectorAll(`.cto-cart-item[data-parent-bundle-key="${itemKey}"]`);
        $bundleItems.forEach(($bundleItem) => {
          $bundleItem.classList.add('active');
        });

        // Update cart-drawer heading message (single/multiple)
        const itemActiveElements = $mainCartItemsWrapper.querySelectorAll('.cto-cart-item.active');
        const cartDrawerHeading = this.querySelector('.js-cto-cart-drawer-heading');
        const itemSingleInCartText = cartDrawerHeading.dataset.singleItemText;
        const itemMultipleInCartText = cartDrawerHeading.dataset.multipleItemsText;

        if ((itemActiveElements.length > 1 || itemQty > 1) && itemMultipleInCartText) {
          cartDrawerHeading.innerHTML = itemMultipleInCartText;
        } else {
          itemSingleInCartText && (cartDrawerHeading.innerHTML = itemSingleInCartText);
        }
      }
    });
  }

  updateItems(itemsData) {
    this.updateCurrentItemsSkus(itemsData);
    this.updateCurrentItemsAll(itemsData);
    fetch(`${window.Shopify.routes.root}cart?view=cto-cart-drawer-items`)
    .then((response) => response.text())
    .then((html) => {
      const $items = this.querySelector('.js-cart-items-body') || null;
      if ($items && this.$footer) {
        $items.innerHTML = this.getInnerHTML(html, '.js-cart-items-body');
        this.$footer.innerHTML = this.getInnerHTML(html, '.js-cart-items-footer');
        const productTemplate = document.body.dataset.template;
        // get new cto item key from updated liquid
        let newCto;

        if (productTemplate.includes('pdp-2.0') || productTemplate.includes('plp-new')) {
          newCto = this.querySelector('.cto-cart-item[data-cto-product="true"]');
        } else {
          newCto = this.querySelector('.cto-cart-item[data-added-from-pdp="true"]');
        }

        const {ctoCartItemKey} = newCto?.dataset || {};

        if (ctoCartItemKey) {
          this.updateCtoItemKey(ctoCartItemKey);
        }

        const {token} = newCto?.dataset || {};
        const newCtoItems = this.querySelectorAll(`.cto-cart-item[data-cto-product="true"][data-token="${token}"]`);
        const ctoItemKeys = Array.from(newCtoItems)
          .map(item => item.dataset.ctoCartItemKey);

        if (ctoItemKeys.length > 0) {
          this.updateSkuKeyPair(ctoItemKeys, itemsData);
        } else if (ctoCartItemKey) {
          this.updateSkuKeyPair([ctoCartItemKey], itemsData);
        }

        this.updateLatestCartItems();
        this.addEvents();
        this.open();
      }

      // scroll to top
      this.querySelector('cto-cart-drawer cart-items').scroll(0, 0);
    })
    .catch((error) => console.error({message: 'Error updating items', error: error}));
  }

  updateCurrentItemsSkus(itemsData) {
    const ctoItem = itemsData?.find((item) => item.properties?._cto_product || item.properties?._added_from_pdp);
    const ctoItemSku = ctoItem?.sku;
    if (!ctoItemSku) {
      itemsData?.forEach((item) => {
        this.currentItemsSkus[item.sku] = true;
        this.currentSkuKeyPair[item.key] = item.sku;
      });
    } else {
      this.updateCtoItemKey(ctoItem.key);
      this.currentItemsSkus = {};
      this.currentSkuKeyPair = {};
      itemsData?.forEach((item) => {
        this.currentItemsSkus[item.sku] = true;
        this.currentSkuKeyPair[item.key] = item.sku;
      });
    }
  }

  updateCurrentItemsAll(itemsData) {
    itemsData?.forEach((item) => {
      this.currentItemsAll[item.key] = item;
    });
  }

  updateCtoItemKey(ctoItemKey) {
    this.ctoItemKey = ctoItemKey;
  }

  updateSkuKeyPair(ctoItemKeys, itemsData) {
    ctoItemKeys.forEach(ctoItemKey => {
      const item = this.querySelector(`.cto-cart-item[data-cto-cart-item-key="${ctoItemKey}"]`);
      const itemSku = item?.getAttribute('data-sku');
      const itemUuid = item?.getAttribute('data-gwp_key');
      const itemGwpSku = item?.getAttribute('data-gwp_sku');
      const isPrimaryProduct = itemsData?.some(product => product.sku === itemSku);
      const isFbtWithoutCtoProduct = itemsData?.length > 0 && itemsData?.every(item => 
        !item.properties?._cto_product && 
        item.properties?._added_from_fbt === 'true'
      );

      if (itemSku && !isPrimaryProduct && !isFbtWithoutCtoProduct) {
        this.currentSkuKeyPair[ctoItemKey] = itemSku;
      }

      if (!isPrimaryProduct && itemUuid && itemGwpSku) {
        const itemGwpSkuList = itemGwpSku.split(',').map(sku => sku.trim());

        itemGwpSkuList.forEach(gwpSku => {
          if (gwpSku) {
            const ctoItemGwpKeyNew =  this.querySelector(`.cto-cart-item[data-sku="${gwpSku}"][data-gwp_key="${itemUuid}"]`)?.getAttribute('data-cto-cart-item-key');
            this.currentSkuKeyPair[ctoItemGwpKeyNew] = gwpSku;
          }
        });
      }
    });
  }

  forceClearCtoItems() {
    this.ctoItemKey = null;
    this.currentItemsSkus = {};
    this.currentSkuKeyPair = {};
  }

  addEvents() {
    const $modalTriggers = this.querySelectorAll('.ac-modal-trigger') || [];
    const $nmpTriggers = document.querySelectorAll('.js-net-monthly-pricing-modal-trigger');
    if ($modalTriggers?.length) {
      $modalTriggers.forEach(($trigger) => {
        $trigger.addEventListener('click', (event) => {
          if ($trigger.classList.contains('cart-payment-blocks__link') && $nmpTriggers?.length) {
            $nmpTriggers[0].click();
          }
        });
      });
    }
    if (window.refreshModals) window.refreshModals();
  }

  refreshCarouselEvents() {
    const $offerSection = this.querySelector('.js-cto-offers');
    const $offerItems = $offerSection?.querySelector(`.cto-cart-item.active`)|| null;
    const $alsoLikeCarousel = $offerItems?.querySelector('.js-also-like-carousel') || null;
    const $accessoriesCarousel = $offerItems?.querySelector('.js-product-accessories-carousel') || null;

    $offerSection.classList.remove('hidden');
    // small timeout to make sure the carousel is rendered
    setTimeout(() => {
      if ($alsoLikeCarousel) {
        this.addCaouselEvents($alsoLikeCarousel, 'js-you-also-like-carousel');
      }

      if ($accessoriesCarousel) {
        this.addCaouselEvents($accessoriesCarousel, 'js-accessories-carousel');
      }
    }, 100);
  }

  addCaouselEvents(el, selector) {
    if (!el && !selector) return;
    const $atcButtons = el.querySelectorAll('.slider-form:not(.event-attached)');

    if ($atcButtons?.length > 0) {
      window.quickAddEvents();
    }

    $(`.${selector}`).slick({
      slidesToShow: 3,
      slidesToScroll: 1,
      infinite: false,
      variableWidth: true,
      responsive: [
        {
          breakpoint: 768,
          settings: {
            slidesToShow: 1.45,
            infinite: false,
            variableWidth: false,
          }
        },
      ]
    });
  }
}

customElements.define('cto-cart-drawer', CTOCartDrawer);